/* =============================================================================
 *  TerminalEmailView
 *
 *  Terminal-native replacement for `EmailView`. Prop-identical so hosts
 *  can swap with a single conditional. Body hydration + inline-image
 *  handling + chat-about-email + attachment handling are all preserved
 *  (implementation duplicated from the classic view — the helpers are
 *  pure and stable; drift risk accepted for status-quo fork).
 *
 *  Differences vs. classic:
 *   - Monospace prompt-style header (`$ email INV-44492`)
 *   - Dashed-divider sections (from/to/subject/body split cleanly)
 *   - Bracketed-text actions (`[chat]`, `[reply]` happens via the
 *     panel's header buttons, not this component)
 *   - Attachments render as monospace rows with bracketed `[download]`
 *   - Body iframe still renders with a WHITE background — emails are
 *     authored for light clients and forcing dark-mode styles on third-
 *     party HTML looks terrible (images disappear, text contrast wrong).
 *     The chrome around the iframe is terminal; the iframe content is
 *     the sender's faithful rendition.
 * ============================================================================= */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../../../firebase';
import { useNavigate } from 'react-router-dom';
import { PanelEmailData, useEmailPanel } from '../contexts/EmailPanelContext';
import { useAppData } from '../../../providers/AppDataProvider';
import { toast } from 'react-toastify';
// useChat + StartChatFromEmailModal removed 2026-05 — Chat-about-email retired.
import { authFetch } from '../../../utils/cloudFunctionsAuth';
import sanitizeHtml from '../../../utils/sanitizeHtml';
import { isDateOverdue, todayAdelaide } from '../../../utils/dateUtils';
import {
  getCachedGraphEmailBody, getOrFetchGraphEmailBody, warmGraphEmailBodyCache,
} from '../utils/graphEmailBodyCache';
import {
  resolveInlineGraphImages,
  stripCidImages,
  fetchGraphAttachmentsList,
} from '../utils/inlineImageResolver';
import {
  QUOTED_COLLAPSE_CSS,
  QUOTED_COLLAPSED_CLASS,
  collapseQuotedHtml,
  collapseQuotedText,
  setQuotedCollapsed,
} from '../utils/quotedText';
import { isHttpsUrl, previewVerdict, safeFilename } from '../utils/attachmentPreview';
import { locationOf, moveEmail, openInOutlook, restoreEmail } from '../utils/mailActions';
import type { EmailLocation, MoveSubject } from '../utils/mailActions';
import { showMoveUndoToast } from '../utils/undoToast';
import { replyRecipients } from '../utils/replyRecipients';
import { DEFAULT_FEED_MAILBOXES } from '../utils/feedWindow';
import { OWNER_ONLY_REASON } from '../utils/markEmailRead';
import AttachmentPreviewModal, { openPreviewTab } from './AttachmentPreviewModal';
import type { PreviewTarget } from './AttachmentPreviewModal';
import './TerminalEmailView.scss';

interface AttachmentData {
  id?: string;
  filename: string;
  contentType?: string;
  url?: string;
  preview?: string;
  size?: number | null;
  isInline?: boolean;
  contentId?: string | null;
}

// ── Pure helpers (duplicated from classic EmailView) ────────────────────
// These are module-level + pure. Matching the fork's duplication philosophy;
// drift risk is low because email sanitisation changes rarely. If they ever
// become fragile, the right move is to extract to `mail/utils/emailRender.ts`
// and have both views import from there.

/** Wraps email body HTML in a white-background iframe document so third-
 *  party emails render as authored. Dark-mode the chrome, not the email.
 *  `collapseQuoted`: the body carries folded quoted history (quotedText.ts);
 *  it starts hidden, the reader's toggle flips the class on this <body>.
 *  `allowRemoteImages`: the owner chose [load images]. Until then the document
 *  carries an img-src policy (REMOTE_IMG_CSP_META) that backs up
 *  blockRemoteImages. */
const getStyledHtml = (content: string, collapseQuoted = false, allowRemoteImages = false) => `
<!DOCTYPE html>
<html>
  <head>
    ${allowRemoteImages ? '' : REMOTE_IMG_CSP_META}
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <style>
      /* NOTE: the doctype above matters — without it the iframe renders in
         quirks mode, where body.scrollHeight reports max(content, viewport)
         and the auto-height measurement can only ever ratchet UP. */
      html, body {
        max-width: 100%;
        overflow-x: hidden;
        /* Stop iOS text-autosizing from randomly inflating fonts in wide layouts. */
        -webkit-text-size-adjust: 100%;
      }
      body {
        background-color: #ffffff;
        color: #212529;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
                     "Helvetica Neue", Arial, sans-serif;
        margin: 0;
        padding: 15px;
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
      }
      img { max-width: 100%; height: auto; }
      table { max-width: 100% !important; }
      pre { white-space: pre-wrap; max-width: 100%; }
      /* Phone-width iframes only (the iframe viewport = the reader width):
         neutralise fixed-width (600px+) marketing-email layouts so the right
         half of the email isn't unreachable at 375px. Desktop readers are
         wide enough — applying these globally collapsed centered fixed-width
         emails into shrink-to-fit soup. */
      @media (max-width: 480px) {
        table { width: auto !important; }
        td, th { max-width: 100%; word-break: break-word; }
        [style*="min-width"] { min-width: 0 !important; }
      }
      ${QUOTED_COLLAPSE_CSS}
    </style>
    <base target="_blank">
  </head>
  <body${collapseQuoted ? ` class="${QUOTED_COLLAPSED_CLASS}"` : ''}>${content}</body>
</html>`;

// DOMPurify (shared util) — replaces the old regex scrub. Forbids
// script/iframe/object/embed/form/base/meta/link + strips handlers + js:/vbscript:
// URIs; keeps cid: refs (resolved to data: AFTER this) intact.
const sanitizeEmailHtml = (html: string): string => (html ? sanitizeHtml(html) : '');

// Remote images are how a sender learns the owner opened a message (IP address,
// time, client), so until the owner picks [load images] nothing in an email
// may make the browser fetch one. Two layers, because each alone has gaps:
//   1. blockRemoteImages rewrites the markup (below) so the banner count is
//      truthful and the real URL is kept in data-blocked-src.
//   2. REMOTE_IMG_CSP_META gives the iframe document an img-src policy that
//      only allows data:/blob: images. It is the enforcement for everything the
//      rewrite cannot reach (CSS url() in <style> blocks, exotic attribute
//      spellings, an unparseable body). The app's own CSP has no img-src, and
//      the iframe sandbox does not stop image loads.
// (Fonts, @import and media are not images and are not covered by either.)
const REMOTE_IMG_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
const REMOTE_IMG_CSP_META = '<meta http-equiv="Content-Security-Policy" content="img-src data: blob:">';

// Only these stay: images we inlined ourselves (data:/blob:) and an unresolved
// cid: (nothing to fetch). Everything else - http(s), protocol-relative
// //host, a relative path (the iframe's base URL is this app) - can make the
// browser fetch, so it counts as remote. An allow-list, not an "is it http(s)"
// test: browsers drop tabs/newlines and leading control characters inside a
// URL and read \ as /, so a deny-list pattern is easy to sidestep.
const LOCAL_IMG_URL_RE = /^(?:data|blob|cid):/i;
const isBlockableImgUrl = (raw: string | null | undefined): boolean => {
  if (raw == null) return false;
  const v = raw.replace(/[\t\n\r]/g, '');
  let i = 0;
  while (i < v.length && v.charCodeAt(i) <= 0x20) i++;
  const rest = v.slice(i);
  return rest !== '' && !LOCAL_IMG_URL_RE.test(rest);
};

// url(...) in an inline style (background-image and friends).
const CSS_URL_RE = /url\(\s*("[^"]*"|'[^']*'|[^)]*)\s*\)/gi;
const blockCssUrls = (css: string): { css: string; hit: boolean } => {
  let hit = false;
  const out = css.replace(CSS_URL_RE, (m, inner: string) => {
    const target = inner.replace(/^["']|["']$/g, '').trim();
    if (!isBlockableImgUrl(target)) return m;
    hit = true;
    return 'url()';
  });
  return { css: out, hit };
};

// Cheap gate so a body with nothing image-like never pays for a DOM parse.
const IMAGE_HINT_RE = /<(?:img|image|source|input|video)\b|\b(?:background|srcset|style)\s*=/i;

// Block remote images in an ALREADY SANITISED body. Works on a parsed DOM, not
// on the serialised string: a tag regex is beaten by `<img alt=">" src=...>`
// (the `>` ends the match before the src) and by attribute spellings a regex
// has to guess at. Returns the input untouched when nothing was blocked;
// otherwise the DOM is re-serialised and sanitised once more before it leaves
// here (same rule as the quoted-text fold). `blocked` counts elements.
const blockRemoteImages = (html: string): { html: string; blocked: number } => {
  const unchanged = { html, blocked: 0 };
  if (!html || !IMAGE_HINT_RE.test(html) || typeof DOMParser === 'undefined') return unchanged;
  try {
    const body = new DOMParser().parseFromString(html, 'text/html').body;
    if (!body) return unchanged;
    const hit = new Set<Element>();

    // <img src> (and <input src>, which loads when type=image): swap in the
    // placeholder and keep the real URL where a later "load images" can see it.
    body.querySelectorAll('img[src], input[src]').forEach((el) => {
      const src = el.getAttribute('src');
      if (!isBlockableImgUrl(src)) return;
      el.setAttribute('data-blocked-src', src as string);
      el.setAttribute('src', REMOTE_IMG_PLACEHOLDER);
      hit.add(el);
    });
    // srcset (<img>, <picture><source>) is a second way to name an image. A
    // srcset holding only data: candidates is not worth parsing for: drop it.
    body.querySelectorAll('[srcset]').forEach((el) => {
      el.setAttribute('data-blocked-srcset', el.getAttribute('srcset') || '');
      el.removeAttribute('srcset');
      hit.add(el.tagName === 'SOURCE' ? (el.closest('picture') || el) : el);
    });
    // Legacy background="..." on table cells, plus <video poster>.
    body.querySelectorAll('[background], video[poster]').forEach((el) => {
      ['background', 'poster'].forEach((attr) => {
        const v = el.getAttribute(attr);
        if (!isBlockableImgUrl(v)) return;
        el.setAttribute(`data-blocked-${attr}`, v as string);
        el.removeAttribute(attr);
        hit.add(el);
      });
    });
    // Inline style url().
    body.querySelectorAll('[style]').forEach((el) => {
      const r = blockCssUrls(el.getAttribute('style') || '');
      if (!r.hit) return;
      el.setAttribute('style', r.css);
      hit.add(el);
    });

    if (!hit.size) return unchanged;
    return { html: sanitizeEmailHtml(body.innerHTML), blocked: hit.size };
  } catch {
    // Unparseable: the iframe's img-src policy is what keeps remote loads out.
    return unchanged;
  }
};

// Escape a PLAIN-TEXT email body before splicing it into markup. Without this a
// text-only email whose body literally contains `<img src=x onerror=…>` becomes
// live markup — and email bodies are attacker-controlled.
const escapeText = (s: string): string =>
  String(s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Job statuses considered "active" for the context rail (mirrors the feed).
const ACTIVE_JOB_STATUSES = ['Open', 'Active', 'Pending', 'Awaiting Client Response'];
// Compact money for the context rail.
const ctxMoney = (n: number): string => {
  const v = Math.abs(n);
  if (v >= 1000) return `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  return `$${Math.round(v)}`;
};

// Endpoint for the inline quick-reply. Same function the full composer uses —
// threading + the durable job/customer link hint come for free.
const SEND_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/sendEmailGraphHttp';
const ACCOUNTS_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/getGraphMailAccountsHttp';
const DRAFT_REPLY_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/draftEmailReplyHttp';
const MANAGE_TASKS_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/manageAndreiTasks';

// Enabled sender accounts, module-cached so opening a reader doesn't re-fetch.
// Used to resolve the quick-reply's send account: the receiving mailbox if it's
// a sendable account, else the first enabled one (mirrors the composer, which
// never blindly trusts the receiving mailbox as a send account).
let cachedSendAccounts: Array<{ id: string; mailbox: string }> | null = null;
async function fetchSendAccounts(): Promise<Array<{ id: string; mailbox: string }>> {
  if (cachedSendAccounts) return cachedSendAccounts;
  try {
    const resp = await authFetch(ACCOUNTS_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    });
    const data = await resp.json().catch(() => ({}));
    // A failed lookup must not be cached: the empty list would stick for the
    // whole session and every quick reply would say "no sendable account".
    if (!resp.ok || data?.ok === false) return [];
    const list = Array.isArray(data?.accounts)
      ? data.accounts.filter((a: any) => a?.isEnabled !== false).map((a: any) => ({ id: a.id, mailbox: a.mailbox }))
      : [];
    cachedSendAccounts = list;
    return list;
  } catch { return []; }
}
const formatReplySubject = (s: string): string =>
  /^\s*re:/i.test(s) ? s : `Re: ${s}`;
// Turn a plain-text quick reply into minimal, safe HTML (escaped, newlines→<br>).
const quickReplyHtml = (text: string): string =>
  `<div>${escapeText(text).replace(/\n/g, '<br>')}</div>`;

// Cid + inline-image helpers were lifted out to
// ../utils/inlineImageResolver — single source of truth shared with
// EmailView and the JobWorkspace thread pane.

// ── Small presentation helpers ──────────────────────────────────────────
const getDisplaySubject = (d: any): string => {
  const s = d?.subject || d?.title || d?.emailSubject;
  return s ? String(s) : '(no subject)';
};

const getDisplayFromList = (d: any): { address: string; name?: string }[] => {
  const v = d?.from;
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return [];
    return s.includes('@') ? [{ address: s }] : [{ address: '', name: s }];
  }
  const sender = typeof d?.sender === 'string' ? d.sender.trim() : '';
  if (sender) return [{ address: '', name: sender }];
  return [];
};

const formatAddresses = (field: any): string => {
  if (!field) return '';
  if (Array.isArray(field)) {
    return field
      .map((i: any) => (typeof i === 'string' ? i : i?.address || i?.text || ''))
      .filter(Boolean).join(', ');
  }
  if (typeof field === 'string') return field;
  if (typeof field === 'object') return field?.address || field?.text || '';
  return '';
};

const formatDate = (ts: any): string => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-AU', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

// ── Reader actions: archive / open in Outlook / attachment bytes ──────────
const DOWNLOAD_ATTACHMENT_URL = 'https://us-central1-web-construct-app.cloudfunctions.net/downloadGraphAttachment';

/** Bytes of one Graph attachment for the preview (same endpoint as the
 *  download button, but the blob is shown instead of saved). */
async function fetchGraphAttachmentBlob(emailId: string, attachmentId: string, signal: AbortSignal): Promise<Blob> {
  const resp = await authFetch(DOWNLOAD_ATTACHMENT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emailId, attachmentId }),
    signal,
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data?.error || `Download failed (HTTP ${resp.status})`);
  }
  // Attached emails / reference attachments answer with JSON, not file bytes.
  if (/json/i.test(resp.headers.get('content-type') || '')) throw new Error('this attachment has no file to preview');
  return resp.blob();
}

/** The fields a mail move needs, from whatever email shape the reader got. */
const moveSubjectOf = (d: any): MoveSubject => ({
  id: String(d?.emailId || d?.id || ''),
  folder: typeof d?.folder === 'string' ? d.folder : undefined,
  deleted: d?.deleted === true ? true : undefined,
  outlookFolderId: typeof d?.outlookFolderId === 'string' ? d.outlookFolderId : undefined,
  outlookFolderName: typeof d?.outlookFolderName === 'string' ? d.outlookFolderName : undefined,
  outlookFolderPath: typeof d?.outlookFolderPath === 'string' ? d.outlookFolderPath : undefined,
  emailType: typeof d?.emailType === 'string' ? d.emailType : undefined,
  quarantined: d?.quarantined === true ? true : undefined,
});

// Archive is offered from folders it can be UNDONE from: Deleted Items is not
// restorable from here (mailActions), Sent/Drafts cannot be moved, Archive is
// already there.
const ARCHIVABLE_FROM = ['inbox', 'junk', 'filed'];
const canArchiveEmail = (d: any): boolean => {
  if (!d) return false;
  const subject = moveSubjectOf(d);
  return !!subject.id && ARCHIVABLE_FROM.indexOf(locationOf(subject).folder) !== -1;
};

// A move that changed nothing: the server answers {previous === current} when the
// message was already at the destination (the reader's row can be a stale snapshot,
// or another device got there first).
const sameLocation = (a: EmailLocation, b: EmailLocation): boolean =>
  a.folder === b.folder && (a.folder !== 'filed' || (a.outlookFolderId || '') === (b.outlookFolderId || ''));

// The row's cached Outlook link, but only while it is known to belong to the
// message's CURRENT Graph id. The link embeds that id and a move (in the app, or
// in Outlook via the sync) rotates it, so a link stored for an older id is dead.
// Same rule as getGraphEmailWebLink; one more case is refused here: a link with a
// recorded id when the row carries no graph.id to compare it with (partial rows
// from dashboards) - the server has the whole doc and decides. Refusing only
// costs a round trip; using a dead link opens an error page.
const usableWebLink = (d: any): string | undefined => {
  const link = typeof d?.webLink === 'string' ? d.webLink : '';
  if (!link) return undefined;
  const forId = typeof d?.webLinkForId === 'string' ? d.webLinkForId.trim() : '';
  const graphId = typeof d?.graph?.id === 'string' ? d.graph.id.trim() : '';
  if (forId && forId !== graphId) return undefined;
  return link;
};

// A message only exists in Outlook if it came through Graph; a draft with no
// Graph id has no Outlook copy to open.
const canOpenInOutlook = (d: any): boolean => {
  if (!d) return false;
  const hasGraphId = !!d?.graph?.id;
  const isGraphMail = d?.source === 'graph' || hasGraphId;
  const draftWithoutOutlookCopy = d?.folder === 'drafts' && !hasGraphId;
  return !!(d?.emailId || d?.id) && isGraphMail && !draftWithoutOutlookCopy;
};

// ── Component ───────────────────────────────────────────────────────────
interface TerminalEmailViewProps {
  emailData: (PanelEmailData & {
    customerId?: string | null;
    attachments?: AttachmentData[];
  }) | null;
}

const TerminalEmailView: React.FC<TerminalEmailViewProps> = ({ emailData }) => {
  const [htmlContent, setHtmlContent]       = useState<string | null>(null);
  const [loading, setLoading]               = useState(true);
  const [isBodyLoading, setIsBodyLoading]   = useState(false);
  const [error, setError]                   = useState<string | null>(null);
  const [bodyKind, setBodyKind]             = useState<'html' | 'text' | null>(null);
  const [loadingFormatted, setLoadingFormatted] = useState(false);
  const [copiedEmail, setCopiedEmail]       = useState<string | null>(null);
  const [graphAttachments, setGraphAttachments]     = useState<AttachmentData[] | null>(null);
  const [showAttSection, setShowAttSection]         = useState(false);
  // Remote images stay blocked until the owner loads them for THIS email. Stored as the
  // id they were allowed for (not a boolean that an effect resets when another email
  // opens): an effect-based reset runs AFTER the body-hydration effect in the same
  // commit, and when an email already has its html hydration finishes synchronously,
  // so the reset wiped the blocked count - images blocked, banner and [load images]
  // button gone.
  const [remoteAllowedFor, setRemoteAllowedFor]     = useState<string | null>(null);
  const remoteKey = String(emailData?.id || '');
  const blockRemote = remoteAllowedFor !== remoteKey;
  const [remoteBlockedCount, setRemoteBlockedCount] = useState(0);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [downloadingId, setDownloadingId]   = useState<string | null>(null);
  const [attachmentDownloadErrors, setAttachmentDownloadErrors] = useState<Record<string, string>>({});
  const [attachmentsError, setAttachmentsError] = useState<string | null>(null);
  // Attached EMAILS (Graph itemAttachment / message/rfc822) — a client
  // forwarding another email as an attachment. These have no downloadable
  // bytes; clicking one fetches the nested message and renders it inline,
  // keyed by attachment id.
  const [nestedEmails, setNestedEmails] = useState<Record<string, {
    open: boolean;
    loading?: boolean;
    error?: string;
    item?: {
      subject: string;
      from: { name: string; address: string } | null;
      to: Array<{ name: string; address: string }>;
      cc: Array<{ name: string; address: string }>;
      sentDateTime: string | null;
      bodyContentType: string;
      body: string;
      hasAttachments: boolean;
    };
    showRemote?: boolean;
  }>>({});

  // Quoted-history fold (quotedText.ts). `hasQuoted` = this body has a folded
  // reply chain; `quotedExpanded` = the owner opened it. Per email: reset below
  // whenever a different email opens. Mirrored in refs so the (async) body
  // hydration and the iframe onLoad always read the CURRENT choice.
  const [hasQuoted, setHasQuoted]           = useState(false);
  const [quotedExpanded, setQuotedExpanded] = useState(false);
  const hasQuotedRef       = useRef(false);
  const quotedExpandedRef  = useRef(false);
  // Reader actions + attachment preview.
  const [archiving, setArchiving]           = useState(false);
  const archivingRef       = useRef(false);
  const [openingOutlook, setOpeningOutlook] = useState(false);
  const [preview, setPreview]               = useState<PreviewTarget | null>(null);
  const previewSeqRef      = useRef(0);

  const iframeRef                  = useRef<HTMLIFrameElement>(null);
  const heightAdjustmentTimeoutRef = useRef<number | null>(null);
  const latestEmailKeyRef          = useRef<string>('');

  const { closeEmailPanel, setDraftMode, setEmailPanelView } = useEmailPanel();
  const navigate = useNavigate();
  const { customers, jobs: allJobs, invoices } = useAppData();
  const linkedCustomer = emailData?.customerId
    ? customers.find((c: any) => c.id === emailData.customerId)
    : null;

  // ── Business-context rail data ────────────────────────────────────────
  // Outstanding invoices + active jobs for the email's linked customer, so the
  // owner sees "do they owe me / what work is open" right beside their message.
  const ctxCustomerId = (emailData as any)?.customerId || null;
  const ctxInvoices = React.useMemo(() => {
    if (!ctxCustomerId) return [] as any[];
    // Xero-shaped invoices (PascalCase): Status / AmountDue / DueDateString /
    // Contact.ContactID. Overdue is the Adelaide calendar-date test.
    const today = todayAdelaide();
    return (invoices || [])
      .filter((inv: any) => inv?.Contact?.ContactID === ctxCustomerId)
      .map((inv: any) => {
        const status = String(inv.Status || '').toUpperCase();
        const due = Number(inv.AmountDue || 0);
        const dueStr: string = inv.DueDateString || inv.DueDate || '';
        const isOutstanding = due > 0 && status !== 'PAID' && status !== 'VOIDED' && status !== 'DELETED' && status !== 'DRAFT';
        const isOverdue = isOutstanding && isDateOverdue(dueStr);
        const overdueDays = isOverdue
          ? Math.max(1, Math.round((Date.parse(today) - Date.parse(String(dueStr).slice(0, 10))) / 86400000))
          : 0;
        return {
          inv, due, status, isOutstanding, isOverdue, overdueDays,
          id: inv.InvoiceID || inv.id, number: inv.InvoiceNumber || inv.InvoiceID || inv.id,
        };
      })
      .filter((x: any) => x.isOutstanding)
      .sort((a: any, b: any) => (b.isOverdue ? 1 : 0) - (a.isOverdue ? 1 : 0) || b.due - a.due)
      .slice(0, 6);
  }, [invoices, ctxCustomerId]);
  const ctxOverdueTotal = ctxInvoices.reduce((n: number, x: any) => n + (x.isOverdue ? x.due : 0), 0);
  const ctxOutstandingTotal = ctxInvoices.reduce((n: number, x: any) => n + x.due, 0);
  const ctxJobs = React.useMemo(() => {
    if (!ctxCustomerId) return [] as any[];
    return (allJobs || [])
      .filter((j: any) => {
        if (!ACTIVE_JOB_STATUSES.includes(j.status)) return false;
        const cid = j.customerId;
        return Array.isArray(cid) ? cid.includes(ctxCustomerId) : cid === ctxCustomerId;
      })
      .slice(0, 6);
  }, [allJobs, ctxCustomerId]);

  // Resolve the quick-reply's send account (see fetchSendAccounts above).
  const [sendAccounts, setSendAccounts] = useState<Array<{ id: string; mailbox: string }>>(cachedSendAccounts || []);
  useEffect(() => {
    let cancelled = false;
    fetchSendAccounts().then((l) => { if (!cancelled && l.length) setSendAccounts(l); });
    return () => { cancelled = true; };
  }, []);
  const replyMailbox = String((emailData as any)?.mailbox || '').trim();
  const resolvedAccountId = React.useMemo(() => {
    const mb = replyMailbox.toLowerCase();
    if (!mb) return null;
    if (!sendAccounts.length) return replyMailbox; // optimistic before accounts load
    const match = sendAccounts.find((a) => String(a.mailbox || a.id || '').toLowerCase() === mb);
    if (match) return match.id || match.mailbox;
    // Receiving mailbox isn't a sendable account — fall back to the first enabled.
    return sendAccounts[0]?.id || sendAccounts[0]?.mailbox || null;
  }, [sendAccounts, replyMailbox]);

  // Who the quick reply goes to. replyRecipients() decides from the mail's own
  // direction (direction / emailType / mailbox / folder), so the owner's copy of
  // a sent mail answers its RECIPIENTS even when that copy sits in the inbox or
  // deleted folder - replying to the From there would mail the owner himself.
  // Who is "us" takes three separate answers (see prefillReplyRecipients in
  // TerminalSendEmailForm) and blurring them is how the wrong person gets mailed:
  //  - ownAddresses: for FILTERING, kept NARROW - the signed-in user, the mail's own
  //    mailbox and the account this reply goes out FROM. Ben and Lidia are two people
  //    sharing the business, so lidia@ must not be "us" for Ben's reply to a mail he
  //    sent to [client, lidia@] (every original recipient keeps the reply).
  //  - selfAddresses: the signed-in person only. Their mail is theirs wherever its copy
  //    sits, e.g. the copy that landed in Lidia's mailbox when Ben cc'd her (from=ben@,
  //    mailbox=lidia@, direction 'in'): without this it reads as inbound and the quick
  //    reply - sent in one tap, recipients only hinted - mails Ben himself.
  //  - accountAddresses: every send account + feed mailbox, authorship evidence ONLY
  //    for legacy docs with no mailbox. Never used for filtering.
  const quickReplyTo = React.useMemo(() => {
    if (!emailData) return [] as string[];
    const me = auth?.currentUser?.email;
    const fromAccount = sendAccounts.find((a) => (a.id || a.mailbox) === resolvedAccountId);
    return replyRecipients(emailData, {
      mode: 'reply',
      ownAddresses: [me, replyMailbox, resolvedAccountId, fromAccount?.mailbox],
      selfAddresses: [me],
      accountAddresses: [...sendAccounts.flatMap((a) => [a.mailbox, a.id]), ...DEFAULT_FEED_MAILBOXES],
    }).to;
  }, [emailData, replyMailbox, resolvedAccountId, sendAccounts]);

  // ── Inline quick-reply ────────────────────────────────────────────────
  // A lightweight reply box in the reader itself (r to open) — for the fast
  // "got it, thanks" replies that don't need the full composer. Sends via the
  // same endpoint (threaded), then returns to the list (act-then-advance).
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const replyRef = useRef<HTMLTextAreaElement>(null);
  const replySendingRef = useRef(false);
  // Andrei affordances: one-click AI draft-reply + watch-for-reply.
  const [drafting, setDrafting] = useState(false);
  const [watching, setWatching] = useState(false);

  // Reset the reply box + any opened attached-emails whenever a different
  // email opens. Also the quoted-text toggle (per email - the next email
  // starts folded again) and any open attachment preview. Declared BEFORE the
  // body-hydration effect on purpose: it must run first on an email change so
  // hydration reads the reset quotedExpandedRef.
  useEffect(() => {
    setShowReply(false); setReplyText(''); setReplyError(null);
    replySendingRef.current = false; setReplySending(false);
    setNestedEmails({});
    quotedExpandedRef.current = false; setQuotedExpanded(false);
    setPreview(null);
  }, [emailData?.id]);

  // ── Iframe auto-height (debounced) ────────────────────────────────────
  const measureIframeNow = useCallback(() => {
    try {
      // Zero the height BEFORE measuring so the measurement reflects the
      // content, not the iframe's own stale height — without this the
      // re-measure could only grow (each resize added +30px, never able
      // to shrink after rotation or window changes).
      iframeRef.current!.style.height = '0px';
      const h = iframeRef.current!.contentWindow!.document.body.scrollHeight;
      iframeRef.current!.style.height = `${h + 30}px`;
      // Beat the CSS min-height floor: a two-line email otherwise shows
      // a block of empty white iframe inside the dark chrome.
      iframeRef.current!.style.minHeight = '0';
    } catch {
      /* cross-origin — silently skip */
    }
  }, []);

  const adjustIframeHeight = useCallback(() => {
    if (heightAdjustmentTimeoutRef.current) {
      clearTimeout(heightAdjustmentTimeoutRef.current);
    }
    heightAdjustmentTimeoutRef.current = window.setTimeout(() => {
      if (!iframeRef.current?.contentWindow) return;
      requestAnimationFrame(measureIframeNow);
    }, 250);
  }, [measureIframeNow]);

  // Put the iframe document into the owner's current fold state. Called when a
  // body (re)loads - srcDoc always renders folded, but the owner may have
  // opened the quote before e.g. "load images" swapped the document.
  const syncQuotedFold = useCallback(() => {
    if (!hasQuotedRef.current) return;
    try { setQuotedCollapsed(iframeRef.current?.contentDocument, !quotedExpandedRef.current); } catch { /* ignore */ }
  }, []);

  const handleBodyLoad = useCallback(() => {
    syncQuotedFold();
    adjustIframeHeight();
  }, [syncQuotedFold, adjustIframeHeight]);

  // [show / hide quoted text]. The fold is a class flip on the SAME iframe
  // document (srcDoc is not rebuilt, so scroll position and loaded images
  // survive); re-measure at once so the iframe never sits clipped or padded
  // for the debounce window.
  const toggleQuoted = useCallback(() => {
    const next = !quotedExpandedRef.current;
    quotedExpandedRef.current = next;
    setQuotedExpanded(next);
    syncQuotedFold();
    measureIframeNow();
    adjustIframeHeight();
  }, [syncQuotedFold, measureIframeNow, adjustIframeHeight]);

  useEffect(() => () => {
    if (heightAdjustmentTimeoutRef.current) clearTimeout(heightAdjustmentTimeoutRef.current);
  }, []);

  // Re-measure on viewport changes — rotating the phone reflows the email to
  // a new content height, but the iframe kept the stale pixel height
  // (clipping the bottom or leaving a huge blank run).
  useEffect(() => {
    const onResize = () => adjustIframeHeight();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [adjustIframeHeight]);

  // ── Copy email address feedback ───────────────────────────────────────
  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email).then(() => {
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(null), 2000);
    }).catch((err) => console.error('Failed to copy email:', err));
  };

  // Graph attachment + cid resolution helpers now live in
  // ../utils/inlineImageResolver — imported above. The filename + alt
  // fallback strategy for Apple Mail (contentId-stripped) attachments
  // is preserved in the shared util.

  // ── Body hydration ────────────────────────────────────────────────────
  useEffect(() => {
    const run = async () => {
      if (!emailData) {
        setLoading(false);
        setError('no email data');
        return;
      }
      setLoading(true);
      setError(null);
      setHtmlContent(null);
      setBodyKind(null);
      setRemoteBlockedCount(0);
      hasQuotedRef.current = false; setHasQuoted(false);
      try {
        const emailId = String((emailData as any)?.emailId || emailData.id || '');
        latestEmailKeyRef.current = emailId;

        let html = emailData.html || null;
        let text = emailData.text || null;

        const isGraph = (emailData as any)?.source === 'graph';
        const hasGraphMeta = !!(emailData as any)?.graph?.id;
        const canFetchGraph = isGraph || hasGraphMeta;
        const hasFullBody =
          !!(html && html.length > 200) || !!(text && text.length > 300);

        if (canFetchGraph && !hasFullBody && emailId) {
          // Prefer the formatted (HTML) body so emails open straight into
          // formatted view. The cache resolves this from the ingested
          // email_bodies doc first (fast) and only falls back to Graph.
          const cached = getCachedGraphEmailBody(emailId, 'html');
          if (cached?.content) {
            if (cached.contentType === 'html') html = cached.content;
            else                               text = cached.content || text;
          } else {
            setIsBodyLoading(true);
            try {
              const body = await getOrFetchGraphEmailBody(emailId, 'html');
              if (body.contentType === 'html') html = body.content || html;
              else                             text = body.content || text;
              // Do NOT persist back onto the emails doc — bodies live in the
              // separate email_bodies store (written at ingestion) so the feed
              // snapshot, which subscribes to every emails doc, stays light.
            } catch (err) {
              console.warn('[TerminalEmailView] body fetch failed:', err);
            } finally { setIsBodyLoading(false); }
          }
        }

        // Plain-text bodies fold their "> " history here (collapseQuotedText
        // escapes the text itself, so this markup is safe by construction).
        let foldedQuote = false;
        let bodyHtml: string;
        if (html) {
          bodyHtml = sanitizeEmailHtml(html);
        } else if (text) {
          const t = collapseQuotedText(text);
          bodyHtml = t.html;
          foldedQuote = t.hasQuoted;
        } else {
          bodyHtml = '<div>(no content)</div>';
        }

        // Attempt cid: image resolution whenever the body has cid references
        // and we have an emailId — gating on source=graph alone misses emails
        // whose source flag was never written but still have valid graph.id.
        // Fail-soft to stripCidImages on error so the body still renders.
        if (emailId && html && /cid:/i.test(bodyHtml)) {
          try { bodyHtml = await resolveInlineGraphImages(emailId, bodyHtml, { attachments: graphAttachments }); }
          catch (err) {
            console.warn('[TerminalEmailView] cid resolver threw — falling back to strip', err);
            bodyHtml = stripCidImages(bodyHtml);
          }
        }

        // Block remote/tracking images unless the user chose to load them.
        if (html && blockRemote) {
          const r = blockRemoteImages(bodyHtml);
          bodyHtml = r.html;
          setRemoteBlockedCount(r.blocked);
        } else {
          setRemoteBlockedCount(0);
        }

        // Fold the quoted reply history. LAST step, after sanitising + inline
        // image resolution + remote-image blocking, so the fold only ever
        // marks markup that has already been through the pipeline. When
        // something was folded the result is sanitised once more before it
        // goes anywhere near the iframe (the fold re-serialises the DOM).
        if (html) {
          const folded = collapseQuotedHtml(bodyHtml, { subject: getDisplaySubject(emailData) });
          if (folded.hasQuoted) {
            bodyHtml = sanitizeEmailHtml(folded.html);
            foldedQuote = true;
          }
        }
        hasQuotedRef.current = foldedQuote;
        setHasQuoted(foldedQuote);

        setBodyKind(html ? 'html' : (text ? 'text' : null));
        setHtmlContent(getStyledHtml(bodyHtml, foldedQuote && !quotedExpandedRef.current, !blockRemote));
      } catch (e) {
        console.error('[TerminalEmailView] body render failed:', e);
        setError('failed to render email');
        const fallback = emailData.text
          ? `<div style="white-space: pre-wrap;">${escapeText(emailData.text)}</div>`
          : '<div>Error loading.</div>';
        hasQuotedRef.current = false; setHasQuoted(false);
        setBodyKind(emailData.html ? 'html' : (emailData.text ? 'text' : null));
        setHtmlContent(getStyledHtml(fallback));
      }
      setLoading(false);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailData, blockRemote]);

  // ── Load formatted HTML (graph fallback when text-only loaded first) ──
  const loadFormattedHtml = async () => {
    if (!emailData) return;
    const isGraph = (emailData as any)?.source === 'graph';
    if (!isGraph) return;
    const emailId = String((emailData as any)?.emailId || emailData.id || '');
    if (!emailId) return;
    setLoadingFormatted(true);
    try {
      const body = await getOrFetchGraphEmailBody(emailId, 'html');
      if (body.contentType === 'html' && body.content) {
        const sanitized = sanitizeEmailHtml(body.content);
        let resolved  = await resolveInlineGraphImages(emailId, sanitized, { attachments: graphAttachments });
        if (blockRemote) {
          const r = blockRemoteImages(resolved);
          resolved = r.html;
          setRemoteBlockedCount(r.blocked);
        }
        // Same fold + re-sanitise step as the main hydration path.
        const folded = collapseQuotedHtml(resolved, { subject: getDisplaySubject(emailData) });
        if (folded.hasQuoted) resolved = sanitizeEmailHtml(folded.html);
        hasQuotedRef.current = folded.hasQuoted;
        setHasQuoted(folded.hasQuoted);
        setBodyKind('html');
        setHtmlContent(getStyledHtml(resolved, folded.hasQuoted && !quotedExpandedRef.current, !blockRemote));
        // (No persist — bodies live in email_bodies; see note in body hydration.)
      }
    } catch (e) {
      console.error('[TerminalEmailView] formatted fetch failed:', e);
    } finally { setLoadingFormatted(false); }
  };

  // ── Graph attachment list (on demand) ─────────────────────────────────
  // The view can receive PARTIAL email objects (search rows, timelines,
  // notification opens build their own shapes) that lack `hasAttachments` /
  // `source` even when the underlying email doc has them — which silently
  // hid real attachments. When the flag is missing, resolve it from the
  // email doc itself (cheap Firestore read) instead of trusting the caller.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!emailData) return;
      const docId = String(emailData.id || '');
      const isGraph = (emailData as any)?.source === 'graph' || !!(emailData as any)?.graph?.id;
      let hasAtt: boolean | undefined =
        typeof (emailData as any)?.hasAttachments === 'boolean' ? (emailData as any).hasAttachments : undefined;
      if (isGraph && hasAtt === undefined && docId) {
        try {
          const snap = await getDoc(doc(db, 'emails', docId));
          hasAtt = snap.exists() ? (snap.data() as any)?.hasAttachments === true : false;
        } catch { hasAtt = false; }
      }
      if (cancelled) return;
      if (!isGraph || hasAtt !== true) {
        setShowAttSection(false);
        setGraphAttachments(null);
        setAttachmentsError(null);
        return;
      }
      setShowAttSection(true);
      setAttachmentsLoading(true);
      setAttachmentsError(null);
      try {
        const list = await fetchGraphAttachmentsList(docId);
        if (!cancelled) setGraphAttachments(list);
      } catch (e) {
        if (!cancelled) {
          setGraphAttachments([]);
          setAttachmentsError(e instanceof Error ? e.message : String(e));
        }
      } finally { if (!cancelled) setAttachmentsLoading(false); }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailData?.id]);

  const downloadGraphAttachment = async (att: AttachmentData) => {
    if (!emailData?.id || !att.id) return;
    setDownloadingId(att.id);
    setAttachmentDownloadErrors((prev) => {
      if (!prev[att.id!]) return prev;
      const next = { ...prev }; delete next[att.id!]; return next;
    });
    try {
      const resp = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/downloadGraphAttachment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailId: emailData.id, attachmentId: att.id }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.error || `Download failed (HTTP ${resp.status})`);
      }
      const blob = await resp.blob();
      const cd   = resp.headers.get('content-disposition') || '';
      const fn   = cd.match(/filename="([^"]+)"/i)?.[1] || att.filename || 'attachment';
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = fn;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[TerminalEmailView] attachment download failed:', e);
      setAttachmentDownloadErrors((prev) => ({
        ...prev,
        [att.id!]: e instanceof Error ? e.message : 'Download failed',
      }));
    } finally { setDownloadingId(null); }
  };

  // ── Attached emails (Graph itemAttachment) ────────────────────────────
  // A client forwarding another email AS AN ATTACHMENT arrives as an
  // itemAttachment (message/rfc822) — no bytes to download. Clicking one
  // fetches the nested message from Graph and renders it inline, Outlook-
  // style. (Downloading used to hand the user a useless metadata .json.)
  const isAttachedEmail = (att: AttachmentData): boolean => {
    const t = String((att as any).odataType || '').toLowerCase();
    return t.includes('itemattachment')
      || String(att.contentType || '').toLowerCase() === 'message/rfc822';
  };

  const openAttachedEmail = async (att: AttachmentData) => {
    const attId = (att as any).id as string | undefined;
    if (!emailData?.id || !attId) return;
    const existing = nestedEmails[attId];
    if (existing?.loading) return;
    if (existing?.item) {
      // Already loaded — subsequent clicks just toggle visibility.
      setNestedEmails((prev) => ({ ...prev, [attId]: { ...prev[attId], open: !prev[attId].open } }));
      return;
    }
    setNestedEmails((prev) => ({ ...prev, [attId]: { open: true, loading: true } }));
    try {
      const resp = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/downloadGraphAttachment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailId: emailData.id, attachmentId: attId }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.ok !== true || data?.kind !== 'itemAttachment' || !data?.item) {
        throw new Error(data?.error || `couldn't load the attached email (HTTP ${resp.status})`);
      }
      setNestedEmails((prev) => ({ ...prev, [attId]: { open: true, item: data.item } }));
    } catch (e) {
      setNestedEmails((prev) => ({
        ...prev,
        [attId]: { open: true, error: e instanceof Error ? e.message : 'failed to load' },
      }));
    }
  };

  // Send the inline quick-reply via the same endpoint the composer uses.
  const sendQuickReply = useCallback(async () => {
    if (replySendingRef.current || !emailData) return;
    const body = replyText.trim();
    if (!body) { setReplyError('write a reply first'); return; }

    const accountId = resolvedAccountId;
    if (!accountId) { setReplyError('no sendable account for this email — use the composer'); return; }

    // The original sender, or (for mail the owner sent) its recipients -
    // bare validated addresses, see quickReplyTo above.
    const toList = quickReplyTo;
    if (toList.length === 0) { setReplyError('no reply address — use the composer'); return; }

    const payload = {
      accountId,
      to: toList.length === 1 ? toList[0] : toList,
      cc: [], bcc: [],
      subject: formatReplySubject(getDisplaySubject(emailData)),
      html: quickReplyHtml(body),
      text: body,
      replyToGraphId: (emailData as any)?.graph?.id || undefined,
      replyAll: false,
      linkedJobId: (emailData as any)?.linkedJobId || undefined,
      customerId: (emailData as any)?.customerId || undefined,
    };

    replySendingRef.current = true;
    setReplySending(true);
    setReplyError(null);
    try {
      const resp = await authFetch(SEND_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data?.ok) {
        toast.success('Replied ✓');
        setReplyText(''); setShowReply(false);
        closeEmailPanel(); // act-then-advance: back to the list
      } else {
        setReplyError(`reply failed: ${data?.error || `HTTP ${resp.status}`}`);
      }
    } catch (e: any) {
      setReplyError(`reply failed: ${e?.message || 'network error'}`);
    } finally {
      replySendingRef.current = false;
      setReplySending(false);
    }
  }, [emailData, replyText, closeEmailPanel, resolvedAccountId, quickReplyTo]);

  // [✨ draft reply] — ask the model to draft a reply, land it in the composer
  // as an editable DRAFT (never auto-sends; the owner reviews + sends).
  const draftReply = useCallback(async () => {
    if (drafting || !emailData) return;
    const emailId = String((emailData as any).emailId || emailData.id || '');
    if (!emailId) return;
    setDrafting(true);
    try {
      const resp = await authFetch(DRAFT_REPLY_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailId }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok || !data.draftId) throw new Error(data?.error || `HTTP ${resp.status}`);
      const dSnap = await getDoc(doc(db, 'email_drafts', data.draftId));
      if (!dSnap.exists()) throw new Error('draft not found after create');
      const draft = { id: dSnap.id, ...(dSnap.data() as any) };
      setDraftMode('edit', draft as any);
      setEmailPanelView('editDraft');
      toast.success('Draft ready — review & send');
    } catch (e: any) {
      toast.error(`draft reply failed: ${e?.message || 'error'}`);
    } finally {
      setDrafting(false);
    }
  }, [drafting, emailData, setDraftMode, setEmailPanelView]);

  // [👁 watch for reply] — schedule a notify-only no-response watch on this
  // customer via the existing Andrei tasks engine.
  const watchForReply = useCallback(async () => {
    if (watching || !ctxCustomerId) return;
    setWatching(true);
    try {
      const resp = await authFetch(MANAGE_TASKS_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'createWatch',
          customerId: ctxCustomerId,
          days: 3,
          title: `Watch for reply — ${linkedCustomer?.name || linkedCustomer?.company || 'customer'}`,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
      toast.success("Watching — I'll flag it if they don't reply in 3 days");
    } catch (e: any) {
      toast.error(`couldn't set watch: ${e?.message || 'error'}`);
    } finally {
      setWatching(false);
    }
  }, [watching, ctxCustomerId, linkedCustomer]);

  // [archive] - move the open email to Outlook's Archive, offer an 8s undo, and
  // go back to the list the same way delete does (act-then-advance). The move is
  // a server call (real Graph move + doc update, owner-only): nothing is changed
  // locally first, so a failure or a 403 leaves the email exactly where it was.
  const archiveEmail = useCallback(async () => {
    if (archivingRef.current || !emailData || !canArchiveEmail(emailData)) return;
    const subject = moveSubjectOf(emailData);
    archivingRef.current = true;
    setArchiving(true);
    try {
      const result = await moveEmail(subject, 'archive');
      if (!result.ok) {
        toast.error(
          result.forbidden ? OWNER_ONLY_REASON : `couldn't archive: ${result.reason}`,
          { toastId: result.forbidden ? 'mail-owner-only' : 'mail-archive-failed' },
        );
        return;
      }
      if (sameLocation(result.previous, result.current)) {
        // Already archived (stale row, another device, a repeated keypress): nothing
        // moved, so there is nothing to undo - an [undo] here would be a second
        // no-op that reports success while the mail stays archived.
        toast.info('already in archive', { toastId: 'mail-already-archived' });
      } else {
        showMoveUndoToast({
          message: 'archived',
          onUndo: () => restoreEmail(subject, result.previous).then((r) => r.ok),
        });
      }
      closeEmailPanel();
    } catch (e: any) {
      // moveEmail never throws; this is only a last line of defence.
      toast.error(`couldn't archive: ${e?.message || 'error'}`, { toastId: 'mail-archive-failed' });
    } finally {
      archivingRef.current = false;
      setArchiving(false);
    }
  }, [emailData, closeEmailPanel]);

  // [open in outlook]. openInOutlook opens its blank tab synchronously (popup
  // blockers / iOS only allow that inside the tap), so this handler must call
  // it DIRECTLY - no await, no setTimeout in front of it.
  const handleOpenInOutlook = () => {
    if (openingOutlook || !emailData) return;
    const pending = openInOutlook({ ...moveSubjectOf(emailData), webLink: usableWebLink(emailData) });
    setOpeningOutlook(true);
    pending
      .then((r) => { if (!r.ok) toast.error(`couldn't open in outlook: ${r.reason}`, { toastId: 'mail-outlook-failed' }); })
      .catch((e: any) => { toast.error(`couldn't open in outlook: ${e?.message || 'error'}`, { toastId: 'mail-outlook-failed' }); })
      .then(() => setOpeningOutlook(false));
  };

  // ── Attachment preview ────────────────────────────────────────────────
  // Graph list: bytes come from downloadGraphAttachment (owner-only) and are
  // shown in the modal. A PDF opens in a new tab, so that tab is opened HERE,
  // synchronously inside the tap, and handed to the modal to navigate.
  const previewGraphAttachment = (att: AttachmentData) => {
    const emailKey = String(emailData?.id || '');
    const attId = att.id;
    if (!emailKey || !attId) return;
    const verdict = previewVerdict(att);
    if (!verdict.ok) return;
    previewSeqRef.current += 1;
    const target: PreviewTarget = {
      key: `graph:${emailKey}:${attId}:${previewSeqRef.current}`,
      kind: verdict.kind,
      filename: att.filename,
      mime: verdict.mime,
      size: att.size,
      load: (signal) => fetchGraphAttachmentBlob(emailKey, attId, signal),
    };
    if (verdict.kind === 'pdf') target.pdfTab = openPreviewTab();
    setPreview(target);
  };

  // Stored list (legacy/GCS): the https URL is used as-is - an image shows in
  // the modal straight from the URL, a PDF just opens in a new tab.
  const previewStoredAttachment = (att: AttachmentData) => {
    if (!isHttpsUrl(att.url)) return;
    const verdict = previewVerdict(att);
    if (!verdict.ok) return;
    if (verdict.kind === 'pdf') {
      window.open(att.url, '_blank', 'noopener,noreferrer');
      return;
    }
    previewSeqRef.current += 1;
    setPreview({
      key: `stored:${att.url}:${previewSeqRef.current}`,
      kind: 'image',
      filename: att.filename,
      mime: verdict.mime,
      size: att.size,
      url: att.url,
    });
  };

  // Reader keyboard: Esc closes (or collapses an open reply), r opens the quick
  // reply, e archives, Ctrl/Cmd+Enter sends the quick reply. The reader is the
  // only mail surface mounted while an email is open, so a document-level
  // listener is safe.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      // The full composer column can be open beside the reader (both mounted).
      // When it is, the reader must not own Esc/r — otherwise a focused composer
      // button (not a text field) would let Esc collapse the whole panel mid-
      // compose, or `r` steal focus into the inline reply. Checked via the DOM
      // (not emailPanelView — closing the composer doesn't reset that state, so
      // a state-based guard left the reader's keys dead after the first compose).
      if (document.querySelector('.composer-panel-side.open')) return;
      // An attachment preview owns the keyboard while it is open (it handles its
      // own Esc); `e` / `r` must not archive or reply behind it.
      if (document.querySelector('.att-preview')) return;

      const t = ev.target as HTMLElement | null;
      const tag = (t?.tagName || '').toUpperCase();
      const inReply = t === replyRef.current;

      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!t?.isContentEditable;

      if (inReply && (ev.ctrlKey || ev.metaKey) && ev.key === 'Enter') {
        ev.preventDefault(); void sendQuickReply(); return;
      }
      if (ev.key === 'Escape') {
        // In the inline reply → collapse it. In any OTHER text field (e.g. the
        // full composer, which can be open beside the reader) → leave Esc to
        // that field. Otherwise close the reader.
        if (inReply) { ev.preventDefault(); setShowReply(false); (document.activeElement as HTMLElement)?.blur?.(); return; }
        if (typing) return;
        ev.preventDefault(); closeEmailPanel();
        return;
      }
      // Don't hijack typing (incl. the reply box itself).
      if (typing || ev.altKey || ev.ctrlKey || ev.metaKey) return;
      if (ev.key === 'r') {
        ev.preventDefault();
        setShowReply(true);
        window.setTimeout(() => replyRef.current?.focus(), 0);
        return;
      }
      if (ev.key === 'e' && canArchiveEmail(emailData)) {
        ev.preventDefault();
        // Holding the key must archive once: auto-repeat keydowns keep arriving while
        // the panel is still sliding shut over this (now stale) snapshot.
        if (!ev.repeat) void archiveEmail();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showReply, sendQuickReply, closeEmailPanel, archiveEmail, emailData]);

  // Chat-about-email flow retired 2026-05. Andrei is the chat surface.

  if (!emailData) {
    return <div className="term-emailview term-emailview--empty">{error || '(no email)'}</div>;
  }

  // ── Render ────────────────────────────────────────────────────────────
  const displaySubject = getDisplaySubject(emailData);
  const fromList       = getDisplayFromList(emailData);
  const displayTo      = formatAddresses(emailData.to);
  const displayCc      = formatAddresses(emailData.cc);
  const displayBcc     = formatAddresses(emailData.bcc);
  const isGraph        = (emailData as any)?.source === 'graph' || !!(emailData as any)?.graph?.id;
  const canLoadFormatted =
    isGraph && (bodyKind === 'text' || bodyKind === null) && !loading && !loadingFormatted;
  // Quick-reply needs a resolved sendable account (see resolvedAccountId above).
  const canQuickReply = !!resolvedAccountId;
  const canArchive = canArchiveEmail(emailData);
  const canOutlook = canOpenInOutlook(emailData);

  return (
    <div className="term-emailview">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="term-emailview__head">
        <div className="term-emailview__title">
          <span className="term-emailview__prompt">$</span>
          <span className="term-emailview__label">email</span>
          <span className="term-emailview__subject" title={displaySubject}>{displaySubject}</span>
        </div>
        {emailData.customerId && (
          <div className="term-emailview__meta-line">
            <span className="term-emailview__meta-key">linked</span>
            <button
              type="button"
              className="term-emailview__meta-val term-emailview__meta-val--accent"
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}
              title="Open contact"
              onClick={() => { closeEmailPanel(); navigate(`/contacts?id=${emailData.customerId}`); }}
            >
              {linkedCustomer?.name || linkedCustomer?.company || linkedCustomer?.email || emailData.customerId}
            </button>
          </div>
        )}
      </div>

      {/* ── Envelope metadata ─────────────────────────────────────── */}
      <div className="term-emailview__envelope">
        <div className="term-emailview__meta-line">
          <span className="term-emailview__meta-key">from</span>
          <span className="term-emailview__meta-val">
            {fromList.length === 0 ? '—' : fromList.map((f, i) => (
              <React.Fragment key={i}>
                {f.name && f.address ? `${f.name} <` : ''}
                {f.address ? (
                  <button
                    type="button"
                    className="term-emailview__copyable"
                    title="Click to copy address"
                    onClick={(e) => { e.stopPropagation(); handleCopyEmail(f.address); }}
                  >{f.address}</button>
                ) : f.name}
                {f.name && f.address ? '>' : ''}
                {copiedEmail === f.address && (
                  <span className="term-emailview__copied">· copied</span>
                )}
                {i < fromList.length - 1 && ', '}
              </React.Fragment>
            ))}
          </span>
        </div>
        <div className="term-emailview__meta-line">
          <span className="term-emailview__meta-key">to</span>
          <span className="term-emailview__meta-val">{displayTo || '—'}</span>
        </div>
        {displayCc && (
          <div className="term-emailview__meta-line">
            <span className="term-emailview__meta-key">cc</span>
            <span className="term-emailview__meta-val">{displayCc}</span>
          </div>
        )}
        {displayBcc && (
          <div className="term-emailview__meta-line">
            <span className="term-emailview__meta-key">bcc</span>
            <span className="term-emailview__meta-val">{displayBcc}</span>
          </div>
        )}
        <div className="term-emailview__meta-line">
          <span className="term-emailview__meta-key">date</span>
          <span className="term-emailview__meta-val">{formatDate(emailData.date)}</span>
        </div>
        {(canArchive || canOutlook) && (
          <div className="term-emailview__toolbar" role="toolbar" aria-label="Email actions">
            {canArchive && (
              <button
                type="button"
                className="term-emailview__action term-emailview__action--accent"
                onClick={() => void archiveEmail()}
                disabled={archiving}
                title="Move to Archive (press e) - you get a few seconds to undo"
              >
                {archiving ? '[archiving...]' : '[archive]'}
              </button>
            )}
            {canOutlook && (
              <button
                type="button"
                className="term-emailview__action"
                onClick={handleOpenInOutlook}
                disabled={openingOutlook}
                title="Open this message in Outlook on the web (new tab)"
              >
                {openingOutlook ? '[opening...]' : '[open in outlook]'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Business-context rail ──────────────────────────────────────
          The linked customer's money + work status, right beside their mail:
          "do they owe me, what's open". Zero LLM — pure app data. */}
      {linkedCustomer && (ctxInvoices.length > 0 || ctxJobs.length > 0) && (
        <div className="term-emailview__ctx">
          <div className="term-emailview__ctx-head">
            <span className="term-emailview__ctx-title" title={linkedCustomer.company || linkedCustomer.name || ''}>
              {linkedCustomer.name || linkedCustomer.company || 'customer'}
            </span>
            <button
              type="button"
              className="term-emailview__ctx-open"
              onClick={() => { closeEmailPanel(); navigate(`/contacts?id=${ctxCustomerId}`); }}
              title="Open contact"
            >
              open →
            </button>
          </div>

          {ctxInvoices.length > 0 && (
            <div className="term-emailview__ctx-section">
              <div className="term-emailview__ctx-line">
                {ctxOverdueTotal > 0 ? (
                  <span className="term-emailview__ctx-flag term-emailview__ctx-flag--overdue">
                    ⚠ {ctxMoney(ctxOverdueTotal)} overdue
                  </span>
                ) : (
                  <span className="term-emailview__ctx-flag">
                    {ctxMoney(ctxOutstandingTotal)} outstanding
                  </span>
                )}
                <span className="term-emailview__ctx-count">
                  · {ctxInvoices.length} invoice{ctxInvoices.length === 1 ? '' : 's'}
                </span>
              </div>
              <ul className="term-emailview__ctx-list">
                {ctxInvoices.map((x: any) => (
                  <li key={x.id} className="term-emailview__ctx-item">
                    <span className="term-emailview__ctx-item-name">{x.number}</span>
                    <span className="term-emailview__ctx-item-amt">{ctxMoney(x.due)}</span>
                    {x.isOverdue ? (
                      <span className="term-emailview__ctx-item-tag term-emailview__ctx-item-tag--overdue">{x.overdueDays}d overdue</span>
                    ) : (
                      <span className="term-emailview__ctx-item-tag">{String(x.status).toLowerCase()}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {ctxJobs.length > 0 && (
            <div className="term-emailview__ctx-section">
              <div className="term-emailview__ctx-line">
                <span className="term-emailview__ctx-flag term-emailview__ctx-flag--job">
                  ⚙ {ctxJobs.length} active job{ctxJobs.length === 1 ? '' : 's'}
                </span>
              </div>
              <ul className="term-emailview__ctx-list">
                {ctxJobs.map((j: any) => (
                  <li key={j.id} className="term-emailview__ctx-item">
                    <span className="term-emailview__ctx-item-name" title={j.jobLabel || j.title || j.id}>
                      {j.jobLabel || j.title || j.id}
                    </span>
                    <span className="term-emailview__ctx-item-tag">{j.status}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Re-fetch strip — only while swapping an already-rendered body to the
          formatted view. The INITIAL load is shown by the body skeleton below
          (snippet + shimmer), so we don't stack two loaders. */}
      {loadingFormatted && (
        <div className="term-emailview__status">
          <span className="term-emailview__status-dot" />
          <span>loading formatted view…</span>
        </div>
      )}
      {error && !loading && (
        <div className="term-emailview__error">
          <span className="term-emailview__error-glyph">✗</span>
          <span>{error}</span>
        </div>
      )}

      {/* "Load formatted view" sticky toggle — graph text-only path */}
      {canLoadFormatted && (
        <div className="term-emailview__formatted-bar">
          <button
            type="button"
            className="term-emailview__action term-emailview__action--accent"
            onClick={loadFormattedHtml}
            onMouseEnter={() => {
              // Warm the HTML variant on intent (hover) so the click resolves
              // from cache instead of a second Graph round-trip.
              const id = String((emailData as any)?.emailId || emailData.id || '');
              if (id) warmGraphEmailBodyCache(id, 'html');
            }}
            onTouchStart={() => {
              // Touch has no hover — finger-down is the earliest intent signal.
              const id = String((emailData as any)?.emailId || emailData.id || '');
              if (id) warmGraphEmailBodyCache(id, 'html');
            }}
            disabled={loadingFormatted}
          >
            {loadingFormatted ? '[loading formatted…]' : '[load formatted view]'}
          </button>
          <span className="term-emailview__formatted-hint">
            currently showing plain-text for speed.
          </span>
        </div>
      )}

      {/* [chat-about-email] action removed 2026-05. Open Andrei
          via the header [andrei] button instead. */}

      {/* ── Inline attachments (legacy + GCS URLs) ────────────────── */}
      {emailData.attachments && emailData.attachments.length > 0 && (
        <div className="term-emailview__section">
          <div className="term-emailview__section-rule">── attachments ──</div>
          <ul className="term-emailview__attachments">
            {emailData.attachments.map((att, i) => {
              // [preview] only for https URLs of a previewable kind (image/pdf).
              const canPreview = isHttpsUrl(att.url) && previewVerdict(att).ok;
              return (
                <li key={i} className="term-emailview__att">
                  <div className="term-emailview__att-row">
                    <a
                      className="term-emailview__att-link"
                      href={att.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={!att.url ? 'Attachment not directly downloadable' : att.filename}
                      onClick={(e) => { if (!att.url) e.preventDefault(); }}
                    >
                      {att.filename ? safeFilename(att.filename) : '(unnamed)'}
                      {att.contentType && <span className="term-emailview__att-type"> · {att.contentType}</span>}
                    </a>
                    {canPreview && (
                      <button
                        type="button"
                        className="term-emailview__action term-emailview__att-preview"
                        onClick={() => previewStoredAttachment(att)}
                        aria-label={`Preview ${safeFilename(att.filename)}`}
                        title="Preview without downloading"
                      >
                        [preview]
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* ── Graph attachments (on-demand) ─────────────────────────── */}
      {showAttSection && (
        <div className="term-emailview__section">
          <div className="term-emailview__section-rule">── attachments ──</div>
          {attachmentsLoading && (
            <div className="term-emailview__att-note">loading attachments…</div>
          )}
          {!attachmentsLoading && attachmentsError && (
            <div className="term-emailview__error">
              <span className="term-emailview__error-glyph">✗</span>
              <span>couldn't load attachments: {attachmentsError}</span>
            </div>
          )}
          {!attachmentsLoading && graphAttachments && graphAttachments.length === 0 && (
            <div className="term-emailview__att-note">(no downloadable attachments)</div>
          )}
          {!attachmentsLoading && graphAttachments && graphAttachments.length > 0 && (
            <ul className="term-emailview__attachments">
              {graphAttachments.map((att, i) => {
                // Attached EMAIL — opens inline instead of downloading.
                if (isAttachedEmail(att)) {
                  const attId = (att as any).id as string | undefined;
                  const nested = attId ? nestedEmails[attId] : undefined;
                  return (
                    <li key={attId || i} className="term-emailview__att">
                      <button
                        type="button"
                        className="term-emailview__att-link"
                        onClick={() => void openAttachedEmail(att)}
                        disabled={!attId}
                        title="An email attached to this email — view it inline"
                      >
                        ✉ {att.filename || '(attached email)'}
                        <span className="term-emailview__att-type">
                          {' · attached email · '}
                          {nested?.loading ? 'loading…' : nested?.open && nested?.item ? 'hide' : 'view'}
                        </span>
                      </button>
                      {nested?.open && nested.error && (
                        <div className="term-emailview__att-error">✗ {nested.error} — tap to retry</div>
                      )}
                      {nested?.open && nested.item && (() => {
                        const item = nested.item;
                        const esc = (s: string) => s
                          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                        const raw = item.bodyContentType === 'html'
                          ? sanitizeEmailHtml(item.body)
                          : `<div style="white-space:pre-wrap">${esc(item.body)}</div>`;
                        const blockedRes = nested.showRemote ? { html: raw, blocked: 0 } : blockRemoteImages(raw);
                        return (
                          <div className="term-emailview__nested">
                            <div className="term-emailview__nested-meta">
                              <div className="term-emailview__meta-line">
                                <span className="term-emailview__meta-key">from:</span>
                                <span className="term-emailview__meta-val">
                                  {item.from ? `${item.from.name ? item.from.name + ' ' : ''}<${item.from.address}>` : '(unknown)'}
                                </span>
                              </div>
                              <div className="term-emailview__meta-line">
                                <span className="term-emailview__meta-key">to:</span>
                                <span className="term-emailview__meta-val">
                                  {item.to.map((t) => t.address || t.name).filter(Boolean).join(', ') || '—'}
                                </span>
                              </div>
                              {item.sentDateTime && (
                                <div className="term-emailview__meta-line">
                                  <span className="term-emailview__meta-key">date:</span>
                                  <span className="term-emailview__meta-val">{new Date(item.sentDateTime).toLocaleString()}</span>
                                </div>
                              )}
                              <div className="term-emailview__meta-line">
                                <span className="term-emailview__meta-key">subject:</span>
                                <span className="term-emailview__meta-val">{item.subject || '(no subject)'}</span>
                              </div>
                            </div>
                            {blockedRes.blocked > 0 && (
                              <div className="term-emailview__att-note">
                                🛡 {blockedRes.blocked} remote image{blockedRes.blocked === 1 ? '' : 's'} blocked{' '}
                                <button
                                  type="button"
                                  className="term-emailview__att-link"
                                  onClick={() => setNestedEmails((p) => ({ ...p, [attId!]: { ...p[attId!], showRemote: true } }))}
                                >
                                  [load images]
                                </button>
                              </div>
                            )}
                            <iframe
                              className="term-emailview__nested-iframe"
                              srcDoc={getStyledHtml(blockedRes.html, false, !!nested.showRemote)}
                              sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
                              title={`Attached email: ${item.subject || '(no subject)'}`}
                              onLoad={(e) => {
                                const f = e.currentTarget;
                                try {
                                  f.style.height = '0px';
                                  f.style.height = `${(f.contentWindow?.document.body.scrollHeight || 200) + 30}px`;
                                } catch { /* cross-origin — leave the min-height */ }
                              }}
                            />
                            {item.hasAttachments && (
                              <div className="term-emailview__att-note">
                                this attached email has attachments of its own — open the original in Outlook to download those
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </li>
                  );
                }
                const canPreview = !!att.id && previewVerdict(att).ok;
                return (
                  <li key={att.id || i} className="term-emailview__att">
                    <div className="term-emailview__att-row">
                      <button
                        type="button"
                        className="term-emailview__att-link"
                        onClick={() => downloadGraphAttachment(att)}
                        disabled={!att.id || downloadingId === att.id}
                        title={att.filename}
                      >
                        {att.filename ? safeFilename(att.filename) : '(unnamed)'}
                        {att.contentType && <span className="term-emailview__att-type"> · {att.contentType}</span>}
                        {typeof att.size === 'number' && (
                          <span className="term-emailview__att-type"> · {Math.round(att.size / 1024)} KB</span>
                        )}
                        {downloadingId === att.id && <span className="term-emailview__att-type"> · downloading…</span>}
                      </button>
                      {canPreview && (
                        <button
                          type="button"
                          className="term-emailview__action term-emailview__att-preview"
                          onClick={() => previewGraphAttachment(att)}
                          aria-label={`Preview ${safeFilename(att.filename)}`}
                          title="Preview without downloading"
                        >
                          [preview]
                        </button>
                      )}
                    </div>
                    {att.id && attachmentDownloadErrors[att.id] && (
                      <div className="term-emailview__att-error">
                        ✗ {attachmentDownloadErrors[att.id]}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* ── AI summary (already computed at ingestion; zero LLM cost here) ── */}
      {(emailData as any)?.aiSummary && (
        <div
          className="term-emailview__att-note"
          style={{ borderLeft: `3px solid ${(emailData as any)?.aiImportance === 'high' ? '#e06c6c' : '#7fa7c9'}`, paddingLeft: 8, margin: '4px 0 8px' }}
          title="Auto-summary generated when this email arrived"
        >
          <strong style={{ opacity: 0.7 }}>
            {(emailData as any)?.aiImportance === 'high' ? '❗ ' : '✦ '}summary:
          </strong>{' '}
          {(emailData as any).aiSummary}
        </div>
      )}

      {/* ── Remote-image (tracking) blocked banner ─────────────────── */}
      {blockRemote && remoteBlockedCount > 0 && (
        <div className="term-emailview__att-note" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, borderLeft: '3px solid #d8a857', paddingLeft: 8, flexWrap: 'wrap' }}>
          <span>🛡 {remoteBlockedCount} remote image{remoteBlockedCount === 1 ? '' : 's'} blocked (senders can use them to track opens)</span>
          <button type="button" className="term-emailview__att-link" style={{ whiteSpace: 'nowrap' }} onClick={() => setRemoteAllowedFor(remoteKey)}>
            <span className="term-form__bracket">[</span>load images<span className="term-form__bracket">]</span>
          </button>
        </div>
      )}

      {/* ── Body iframe ───────────────────────────────────────────── */}
      <div className="term-emailview__body">
        {htmlContent ? (
          <iframe
            ref={iframeRef}
            className="term-emailview__iframe"
            srcDoc={htmlContent}
            sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
            title="Email content"
            onLoad={handleBodyLoad}
          />
        ) : (loading || isBodyLoading) ? (
          // Optimistic: never a blank wait. Show the feed snippet we already
          // have (faded) under a shimmer skeleton while the full body streams in.
          <div className="term-emailview__body-loading" aria-busy="true">
            {emailData.text ? (
              <div className="term-emailview__body-snippet">{emailData.text}</div>
            ) : null}
            <div className="term-emailview__skeleton" aria-hidden="true">
              <span className="term-emailview__skeleton-line" />
              <span className="term-emailview__skeleton-line" />
              <span className="term-emailview__skeleton-line term-emailview__skeleton-line--short" />
            </div>
          </div>
        ) : (
          <div className="term-emailview__body-placeholder">
            {emailData.text || '(no content)'}
          </div>
        )}
        {/* Quoted reply history is folded inside the iframe (still in the DOM:
            copy/find/inline images keep working); this real <button> toggles it.
            It sits under the iframe, i.e. right where the visible text ends. */}
        {htmlContent && hasQuoted && (
          <button
            type="button"
            className={`term-emailview__quoted-toggle${quotedExpanded ? ' is-open' : ''}`}
            aria-expanded={quotedExpanded}
            onClick={toggleQuoted}
            title={quotedExpanded ? 'Fold the earlier messages in this thread' : 'Show the earlier messages in this thread'}
          >
            {quotedExpanded ? '[hide quoted text]' : '[show quoted text]'}
          </button>
        )}
      </div>

      <AttachmentPreviewModal target={preview} onClose={() => setPreview(null)} />

      {/* ── Inline quick-reply ─────────────────────────────────────── */}
      {(canQuickReply || ctxCustomerId) && (
        <div className="term-emailview__reply">
          {!showReply ? (
            <div className="term-emailview__reply-bar">
              {canQuickReply && (
                <button
                  type="button"
                  className="term-emailview__action term-emailview__action--accent"
                  onClick={() => {
                    // flushSync mounts the textarea INSIDE this tap's gesture
                    // stack — iOS only raises the on-screen keyboard for
                    // user-initiated focus, so the old setTimeout(focus, 0)
                    // showed a focused-looking box with no keyboard.
                    flushSync(() => setShowReply(true));
                    replyRef.current?.focus();
                    replyRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                  }}
                  title="Quick reply (press r)"
                >
                  [reply]
                </button>
              )}
              {canQuickReply && (
                <button
                  type="button"
                  className="term-emailview__action term-emailview__action--accent"
                  onClick={() => void draftReply()}
                  disabled={drafting}
                  title="Let Andrei draft a reply for you to review"
                >
                  {drafting ? '[drafting…]' : '[✨ draft reply]'}
                </button>
              )}
              {ctxCustomerId && (
                <button
                  type="button"
                  className="term-emailview__action"
                  onClick={() => void watchForReply()}
                  disabled={watching}
                  title="Notify me if this customer doesn't reply within 3 days"
                >
                  {watching ? '[…]' : '[👁 watch for reply]'}
                </button>
              )}
            </div>
          ) : (
            <div className="term-emailview__reply-box">
              <textarea
                ref={replyRef}
                className="term-emailview__reply-input"
                value={replyText}
                onChange={(e) => {
                  setReplyText(e.target.value);
                  // Auto-grow: phones have no drag-resize handle, so anything
                  // past ~3 lines scrolled inside a 60px box. Cap at ~40% of
                  // the viewport so it never swallows the screen.
                  const el = e.target;
                  el.style.height = 'auto';
                  el.style.height = `${Math.min(el.scrollHeight, Math.round(window.innerHeight * 0.4))}px`;
                }}
                placeholder={
                  typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches
                    ? 'quick reply…'
                    : 'quick reply… (Ctrl+Enter to send · Esc to cancel)'
                }
                rows={3}
                disabled={replySending}
              />
              {replyError && (
                <div className="term-emailview__error">
                  <span className="term-emailview__error-glyph">✗</span>
                  <span>{replyError}</span>
                </div>
              )}
              <div className="term-emailview__reply-actions">
                <button
                  type="button"
                  className="term-emailview__action term-emailview__action--accent"
                  onClick={() => void sendQuickReply()}
                  disabled={replySending || !replyText.trim()}
                >
                  {replySending ? '[sending…]' : '[send reply →]'}
                </button>
                <button
                  type="button"
                  className="term-emailview__action"
                  onClick={() => { setShowReply(false); setReplyError(null); }}
                  disabled={replySending}
                >
                  [cancel]
                </button>
                <span
                  className="term-emailview__reply-hint"
                  title={quickReplyTo.length > 0 ? `to ${quickReplyTo.join(', ')}` : undefined}
                >
                  from {resolvedAccountId || replyMailbox}
                  {quickReplyTo.length > 0 && ` · to ${quickReplyTo.join(', ')}`}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default TerminalEmailView;
